import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { message, Typography } from 'antd';
import React from 'react';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { usersService } from '@/services/users.service';
import { handleApiError } from '@/utils/errorHandler';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { loadAvatarFromData } from '@/utils/avatarLoader';
import { mapUserDetailToPersonnelDetail } from '@/features/personnel/personnelDataMapper';
import { exportPersonnelToExcel } from '@/features/personnel/exportPersonnelExcel';

const { Text } = Typography;

export interface ChangedField {
  field: string;
  newValue: string;
  oldValue: string;
}

// Helper: chuẩn hóa để so sánh (lowercase + trim)
const normalize = (value: any): string =>
  (value === undefined || value === null ? '' : String(value)).trim().toLowerCase();

// Helper: map một số giá trị để so sánh/hiển thị ổn định (giới tính, trình độ phổ thông)
const formatFieldValue = (fieldName: string, value: any): string => {
  if (value === undefined || value === null) return '';
  const strValue = String(value).trim();
  const lv = strValue.toLowerCase();

  if (fieldName === 'gender') {
    const genderMap: Record<string, string> = { male: 'Nam', female: 'Nữ', other: 'Khác' };
    return genderMap[lv] || strValue;
  }

  if (fieldName === 'generalEducationLevel' || fieldName === 'education.highestLevel') {
    const eduMap: Record<string, string> = {
      primary: 'Tiểu học',
      secondary: 'Trung học cơ sở',
      highschool: 'Trung học phổ thông',
    };
    return eduMap[lv] || strValue;
  }

  return strValue;
};

// So sánh updateData với dữ liệu từ snapshot trước đó (nếu có) hoặc từ lần chỉnh sửa trước
const findChangedFields = (
  updateData: any,
  previousUpdateData: any | null,
  mappedCurrentData: PersonnelDetail,
  previousSnapshot?: any
): ChangedField[] => {
  const changedFields: ChangedField[] = [];
  
  // Map các field từ updateData sang field names trong PersonnelDetail
  const fieldMapping: Record<string, { 
    displayField: string; 
    getOldValue: (data: PersonnelDetail) => any;
    getOldValueFromPrevious: (prevData: any) => any;
  }> = {
    placeOfBirth: { 
      displayField: 'placeOfBirth', 
      getOldValue: (d) => d.placeOfBirth,
      getOldValueFromPrevious: (prev) => prev?.placeOfBirth,
    },
    hometown: { 
      displayField: 'hometown', 
      getOldValue: (d) => d.hometown,
      getOldValueFromPrevious: (prev) => prev?.hometown,
    },
    permanentAddress: { 
      displayField: 'permanentAddress', 
      getOldValue: (d) => d.permanentAddress,
      getOldValueFromPrevious: (prev) => prev?.permanentAddress,
    },
    currentAddress: { 
      displayField: 'address', 
      getOldValue: (d) => d.address,
      getOldValueFromPrevious: (prev) => prev?.currentAddress,
    },
    email: { 
      displayField: 'email', 
      getOldValue: (d) => d.email,
      getOldValueFromPrevious: (prev) => prev?.email,
    },
    phone: { 
      displayField: 'phone', 
      getOldValue: (d) => d.phone,
      getOldValueFromPrevious: (prev) => prev?.phone,
    },
    dateOfBirth: { 
      displayField: 'dateOfBirth', 
      getOldValue: (d) => d.dateOfBirth,
      getOldValueFromPrevious: (prev) => prev?.dateOfBirth,
    },
    gender: { 
      displayField: 'gender', 
      getOldValue: (d) => d.gender,
      getOldValueFromPrevious: (prev) => prev?.gender,
    },
    ethnicity: { 
      displayField: 'ethnicity', 
      getOldValue: (d) => d.ethnicity,
      getOldValueFromPrevious: (prev) => prev?.ethnicity,
    },
    religion: { 
      displayField: 'religion', 
      getOldValue: (d) => d.religion,
      getOldValueFromPrevious: (prev) => prev?.religion,
    },
    nationality: { 
      displayField: 'nationality', 
      getOldValue: (d) => d.nationality,
      getOldValueFromPrevious: (prev) => prev?.nationality,
    },
    personalTaxCode: { 
      displayField: 'taxCode', 
      getOldValue: (d) => d.taxCode,
      getOldValueFromPrevious: (prev) => prev?.personalTaxCode,
    },
    selfDescription: { 
      displayField: 'description', 
      getOldValue: (d) => d.description,
      getOldValueFromPrevious: (prev) => prev?.selfDescription,
    },
    bankAccountNumber: { 
      displayField: 'bankAccount', 
      getOldValue: (d) => d.bankAccount,
      getOldValueFromPrevious: (prev) => prev?.bankAccountNumber,
    },
    bankAccountName: { 
      displayField: 'bankAccountHolder', 
      getOldValue: (d) => d.bankAccountHolder,
      getOldValueFromPrevious: (prev) => prev?.bankAccountName,
    },
    bankName: { 
      displayField: 'bankName', 
      getOldValue: (d) => d.bankName,
      getOldValueFromPrevious: (prev) => prev?.bankName,
    },
    bankBranch: { 
      displayField: 'bankBranch', 
      getOldValue: (d) => d.bankBranch,
      getOldValueFromPrevious: (prev) => prev?.bankBranch,
    },
    position: { 
      displayField: 'jobTitle', 
      getOldValue: (d) => d.jobTitle,
      getOldValueFromPrevious: (prev) => prev?.position,
    },
    generalEducationLevel: { 
      displayField: 'education.highestLevel', 
      getOldValue: (d) => d.education?.highestLevel,
      getOldValueFromPrevious: (prev) => prev?.generalEducationLevel,
    },
    specialization: { 
      displayField: 'education.major', 
      getOldValue: (d) => d.education?.major,
      getOldValueFromPrevious: (prev) => prev?.specialization,
    },
    yearsOfExperience: { 
      displayField: 'education.yearsOfExperience', 
      getOldValue: (d) => d.education?.yearsOfExperience,
      getOldValueFromPrevious: (prev) => prev?.yearsOfExperience,
    },
  };

  // So sánh từng field
  Object.keys(fieldMapping).forEach((updateKey) => {
    const newValue = updateData[updateKey];
    if (newValue !== undefined && newValue !== null) {
      const mapping = fieldMapping[updateKey];
      
      // Ưu tiên dùng snapshot dữ liệu trước khi gửi request (nếu backend trả về)
      const oldValueFromSnapshot = previousSnapshot ? previousSnapshot[updateKey] : undefined;
      const oldValue = oldValueFromSnapshot !== undefined
        ? oldValueFromSnapshot
        : previousUpdateData 
          ? mapping.getOldValueFromPrevious(previousUpdateData)
          : mapping.getOldValue(mappedCurrentData);
      
      // Format giá trị để so sánh
      const formattedNewValue = formatFieldValue(updateKey, newValue);
      const formattedOldValue = formatFieldValue(updateKey, oldValue);

      // So sánh sau khi normalize để tránh lệch do hoa/thường
      if (normalize(formattedNewValue) !== normalize(formattedOldValue)) {
        changedFields.push({
          field: mapping.displayField,
          newValue: formattedNewValue,
          oldValue: formattedOldValue || 'Chưa cập nhật',
        });
      }
    }
  });

  return changedFields;
};

export interface UseEditHistoryDetailResult {
  // data
  data: PersonnelDetail | null;
  changedFields: ChangedField[];
  loading: boolean;
  error: string | null;
  avatarUri: string | null;
  version: number;

  // actions
  handleExport: () => Promise<void>;
  renderValueWithOld: (
    fieldName: string,
    currentValue: any,
    updateDataField?: string
  ) => React.ReactNode;
}

export const useEditHistoryDetail = (): UseEditHistoryDetailResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ version: string }>();
  const state = location.state as { version?: number; requestId?: string } | null;
  
  const [data, setData] = useState<PersonnelDetail | null>(null);
  const [changedFields, setChangedFields] = useState<ChangedField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avatarUri, setAvatarUri] = useState<string | null>(null);
  
  const version = state?.version || parseInt(params.version || '1', 10);
  const requestId = state?.requestId;

  // Fetch data từ API
  useEffect(() => {
    const fetchEditHistoryDetail = async () => {
      if (!requestId) {
        const errorMsg = 'Không tìm thấy thông tin yêu cầu cập nhật';
        setError(errorMsg);
        message.error(errorMsg);
        navigate(-1);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch request detail từ API
        const request = await usersService.getUpdateRequestById(parseInt(requestId, 10));
        
        if (!request) {
          const errorMsg = 'Không tìm thấy yêu cầu cập nhật';
          setError(errorMsg);
          message.error(errorMsg);
          navigate(-1);
          return;
        }

        // Fetch tất cả các lần chỉnh sửa đã được duyệt (approved) của user này
        const allApprovedRequestsResponse = await usersService.getUpdateRequests({
          userId: request.userId,
          status: 'approved',
          limit: 100,
        });
        
        // Sắp xếp theo thời gian tạo từ cũ đến mới (ASC)
        const sortedApprovedRequests = [...allApprovedRequestsResponse.data].sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateA - dateB;
        });
        
        // Tìm index của lần chỉnh sửa hiện tại trong danh sách
        const currentRequestIndex = sortedApprovedRequests.findIndex(
          (req) => req.id === request.id
        );
        
        // Lấy updateData từ lần chỉnh sửa trước đó (nếu có)
        const previousUpdateData = 
          currentRequestIndex > 0 
            ? sortedApprovedRequests[currentRequestIndex - 1].updateData 
            : null;

        // Fetch user detail hiện tại và các dữ liệu liên quan để làm fallback (cho trường hợp lần đầu)
        const [
          currentUserDetail,
          educationHistory,
          workExperience,
          familyMembers,
          certificates,
          partyHistory,
          idCards,
          personalFiles,
          horoscope,
        ] = await Promise.all([
          usersService.getUserById(request.userId),
          usersService.getEducationHistory(request.userId).catch(() => []),
          usersService.getWorkExperience(request.userId).catch(() => []),
          usersService.getFamilyMembers(request.userId).catch(() => []),
          usersService.getCertificates(request.userId).catch(() => []),
          usersService.getPartyHistory(request.userId).catch(() => []),
          usersService.getIdCards(request.userId).catch(() => []),
          usersService.getPersonalFiles(request.userId).catch(() => []),
          usersService.getHoroscope(request.userId).catch(() => null),
        ]);

        // Tìm ID card images theo mốc thời gian của version hiện tại (không lấy ảnh mới nhất)
        const snapshotTime = request.approvedAt || request.createdAt;
        const snapshotTs = snapshotTime ? new Date(snapshotTime).getTime() : Date.now();

        const pickSnapshotCard = (documentTypes: string[]) => {
          const eligible = idCards
            .filter((card: any) => {
              const typeMatched = documentTypes.includes(card.documentType);
              if (!typeMatched) return false;

              const cardTs = card.uploadedAt ? new Date(card.uploadedAt).getTime() : 0;
              if (!cardTs || Number.isNaN(cardTs) || cardTs > snapshotTs) return false;

              const status = String(card.status || '').toLowerCase();
              return status === 'approved' || status === 'replaced' || card.isApproved === true;
            })
            .sort((a: any, b: any) => {
              const aTs = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
              const bTs = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
              return bTs - aTs;
            });

          return eligible[0]?.fileUrl;
        };

        const idCardFront = pickSnapshotCard(['id_card_front']);
        const idCardBack = pickSnapshotCard(['id_card_back']);
        const passportCover = pickSnapshotCard(['passport_cover']);
        const passportPhoto = pickSnapshotCard(['passport_additional', 'passport_photo']);

        // Map current user detail sang PersonnelDetail format
        const mappedCurrentData = mapUserDetailToPersonnelDetail(currentUserDetail, {
          educationHistory: educationHistory.map((edu) => ({
            trainingInstitution: edu.trainingInstitution,
            major: edu.major,
            trainingForm: edu.trainingForm,
            educationLevel: edu.educationLevel,
            fromDate: edu.fromDate,
            toDate: edu.toDate,
          })),
          workExperience: workExperience.map((exp) => ({
            company: exp.company,
            position: exp.position,
            department: exp.department,
            salary: exp.salary,
            fromDate: exp.fromDate,
            toDate: exp.toDate,
          })),
          familyMembers: familyMembers.map((member) => ({
            relationship: member.relationship,
            fullName: member.fullName,
            birthYear: member.birthYear,
            phone: member.phone,
            occupation: member.occupation,
            workplace: member.workplace,
            address: member.address,
          })),
          certificates: certificates.map((cert) => ({
            id: cert.id,
            certificateNumber: cert.certificateNumber,
            type: cert.certificateType,
            issueDate: cert.fromDate,
            expiryDate: cert.toDate,
            notes: cert.notes,
            issuingUnit: cert.issuingUnit,
          })),
          partyHistory: partyHistory.map((party) => ({
            cardNumber: party.cardNumber,
            form: party.form,
            fromDate: party.fromDate,
            toDate: party.toDate,
            admissionPlace: party.admissionPlace,
            transferPlace: party.transferPlace,
          })),
          personalFiles: personalFiles.map((file) => ({
            fileName: file.fileName,
            fileUrl: file.fileUrl,
          })),
          idCardFront,
          idCardBack,
          passportCover,
          passportPhoto,
          horoscope: horoscope?.imageUrl
            ? `${encodeURI(horoscope.imageUrl)}${horoscope.imageUrl.includes('?') ? '&' : '?'}v=${horoscope.uploadedAt ? new Date(horoscope.uploadedAt).getTime() : Date.now()}`
            : undefined,
        });
        
        // Map updateData sang PersonnelDetail format (giá trị mới)
        const updatedUserDetail = {
          ...currentUserDetail,
          ...request.updateData,
        };
        
        const mappedData = mapUserDetailToPersonnelDetail(updatedUserDetail, {
          educationHistory: educationHistory.map((edu) => ({
            trainingInstitution: edu.trainingInstitution,
            major: edu.major,
            trainingForm: edu.trainingForm,
            educationLevel: edu.educationLevel,
            fromDate: edu.fromDate,
            toDate: edu.toDate,
          })),
          workExperience: workExperience.map((exp) => ({
            company: exp.company,
            position: exp.position,
            department: exp.department,
            salary: exp.salary,
            fromDate: exp.fromDate,
            toDate: exp.toDate,
          })),
          familyMembers: familyMembers.map((member) => ({
            relationship: member.relationship,
            fullName: member.fullName,
            birthYear: member.birthYear,
            phone: member.phone,
            occupation: member.occupation,
            workplace: member.workplace,
            address: member.address,
          })),
          certificates: certificates.map((cert) => ({
            id: cert.id,
            certificateNumber: cert.certificateNumber,
            type: cert.certificateType,
            issueDate: cert.fromDate,
            expiryDate: cert.toDate,
            notes: cert.notes,
            issuingUnit: cert.issuingUnit,
          })),
          partyHistory: partyHistory.map((party) => ({
            cardNumber: party.cardNumber,
            form: party.form,
            fromDate: party.fromDate,
            toDate: party.toDate,
            admissionPlace: party.admissionPlace,
            transferPlace: party.transferPlace,
          })),
          personalFiles: personalFiles.map((file) => ({
            fileName: file.fileName,
            fileUrl: file.fileUrl,
          })),
          idCardFront,
          idCardBack,
          passportCover,
          passportPhoto,
          horoscope: horoscope?.imageUrl,
        });
        setData(mappedData);

        // Tìm các field đã thay đổi
        const changed = findChangedFields(
          request.updateData,
          previousUpdateData,
          mappedCurrentData,
          (request as any).previousData || undefined
        );
        setChangedFields(changed);

        // Load avatar
        try {
          const avatar = await loadAvatarFromData(mappedData.avatar);
          setAvatarUri(avatar);
        } catch (err) {
          console.error('Error loading avatar:', err);
          setAvatarUri(null);
        }
      } catch (err: any) {
        console.error('Error fetching edit history detail:', err);
        const errorMsg = err?.message || 'Không thể tải chi tiết lịch sử chỉnh sửa';
        setError(errorMsg);
        handleApiError(err, errorMsg);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchEditHistoryDetail();
  }, [requestId, navigate]);

  const getChangedField = useCallback((fieldName: string): ChangedField | undefined => {
    return changedFields.find((cf) => cf.field === fieldName);
  }, [changedFields]);

  const renderValueWithOld = useCallback((
    fieldName: string,
    currentValue: any,
    _updateDataField?: string
  ): React.ReactNode => {
    const changedField = getChangedField(fieldName);
    
    if (changedField) {
      // Format giá trị mới
      let displayNewValue = changedField.newValue;
      
      // Format đặc biệt cho một số field
      if (fieldName === 'hometown') {
        displayNewValue = displayNewValue.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '');
      }
      
      // Format date
      if (fieldName === 'dateOfBirth' || fieldName === 'startDate') {
        const formatted = formatDateDisplay(displayNewValue);
        if (formatted) displayNewValue = formatted;
      }
      
      // Format gender
      if (fieldName === 'gender') {
        const genderMap: Record<string, string> = {
          'male': 'Nam',
          'female': 'Nữ',
          'other': 'Khác',
        };
        displayNewValue = genderMap[displayNewValue.toLowerCase()] || displayNewValue;
      }
      
      // Format giá trị cũ
      let displayOldValue = changedField.oldValue;
      if (fieldName === 'hometown' && displayOldValue) {
        displayOldValue = displayOldValue.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '');
      }
      
      // Format date cho giá trị cũ
      if (fieldName === 'dateOfBirth' || fieldName === 'startDate') {
        const formatted = formatDateDisplay(displayOldValue);
        if (formatted) displayOldValue = formatted;
      }
      
      // Hiển thị cả giá trị mới và cũ
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '4px' } },
        React.createElement(Text, { style: { fontSize: '16px', fontWeight: 600, color: '#1890ff' } }, displayNewValue),
        React.createElement(Text, { style: { fontSize: '14px', color: '#8c8c8c', textDecoration: 'line-through' } }, displayOldValue || 'Chưa cập nhật')
      );
    }
    
    // Không có thay đổi, hiển thị bình thường (page sẽ tự thêm className)
    return React.createElement(Text, {}, currentValue || 'Chưa cập nhật');
  }, [getChangedField]);

  const handleExport = useCallback(async () => {
    if (!data) {
      message.warning('Không có dữ liệu để xuất file');
      return;
    }

    try {
      // Tạo tên file: Lich_Su_Chinh_Sua_Lan_X_TenNhanSu_MaNhanSu
      const fullName = (data.fullName || 'NhanSu').replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '');
      const employeeCode = (data.employeeCode || 'MaNS').replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '');
      const fileName = `Lich_Su_Chinh_Sua_Lan_${version}_${fullName}_${employeeCode}.xlsx`;
      await exportPersonnelToExcel(data, fileName, changedFields);
      message.success('Xuất file Excel thành công');
    } catch (err: any) {
      console.error('Error exporting file:', err);
      handleApiError(err, 'Không thể xuất file: ' + (err.message || 'Lỗi không xác định'));
    }
  }, [data, changedFields, version]);

  return {
    // data
    data,
    changedFields,
    loading,
    error,
    avatarUri,
    version,

    // actions
    handleExport,
    renderValueWithOld,
  };
};
